import { motion } from "framer-motion";
import { Star, ArrowUpRight, BadgeCheck } from "lucide-react";
import { CLINIC } from "../data/content";
import { Kicker } from "./fx";

const EASE = [0.22, 1, 0.36, 1];

const REVIEWS = [
  {
    name: "Sudipta Patri",
    service: "Root Canal Treatment",
    text: "Dr. Swagat Panda explained every step patiently and made me feel comfortable. The entire procedure was painless and done very smoothly. The clinic was very clean and the staff friendly and professional. Highly recommend him to anyone looking for a skilled and caring endodontist!",
  },
  {
    name: "Litu Pradhan",
    service: "Teeth Whitening",
    text: "I am absolutely thrilled with the result of my teeth whitening! Years of discolouration have vanished and my smile shines with a new glow. The treatment was quick, comfortable and absolutely worth it. Thank you, Dr. Swagat Panda, for my confident smile!",
  },
  {
    name: "Priyatos Sahoo",
    service: "Re-Root Canal Treatment",
    text: "I had earlier got a root canal done elsewhere which unfortunately failed, leading to pain and swelling. I then approached Dr. Swagat Panda for a re-root canal treatment — and the difference in care was clear right from the start.",
  },
  {
    name: "Verified Patient",
    service: "Dental Crowns · via Google",
    text: "While I was in the UK I took the appointment, and when I came to Bhubaneswar for only 7 days he did 2 crowns instead of implants — his main goal was to save the teeth if possible, and he did. Fantastic work despite complications, and barely any pain. Thank you for giving my smile back.",
  },
  {
    name: "Verified Patient",
    service: "Smile Restoration · via Google",
    text: "In my moment of pain Dr. Swagat Panda became the reason I can smile again. You didn't just save a tooth, you saved my entire smile.",
  },
  {
    name: "Verified Patient",
    service: "General Dentistry · via Google",
    text: "I used to be very anxious about dental visits, but Narayan Dental Care completely changed my perception. The doctor made me feel comfortable, and the treatment was smooth and pain-free. The equipment looked modern and well-maintained.",
  },
];

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden>
    <path
      fill="#4285F4"
      d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z"
    />
    <path
      fill="#EA4335"
      d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.97 11.97 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z"
    />
  </svg>
);

const Stars = ({ size = "h-4 w-4" }) => (
  <div className="flex gap-0.5" aria-label="5 out of 5 stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`${size} fill-ember text-ember`} />
    ))}
  </div>
);

export default function Reviews() {
  const doubled = [...REVIEWS, ...REVIEWS];
  return (
    <section
      id="reviews"
      data-testid="google-reviews-section"
      className="relative overflow-hidden border-t border-white/10 bg-[#070B12] py-24 sm:py-28"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 top-0 h-[380px] w-[380px] rounded-full bg-amber-500/[0.07] blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 bottom-0 h-[320px] w-[320px] rounded-full bg-toothcyan/[0.06] blur-3xl"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-12 flex flex-col justify-between gap-8 lg:flex-row lg:items-end"
        >
          <div>
            <Kicker>Patient Stories</Kicker>
            <h2 className="font-serif text-3xl font-light tracking-tight text-white sm:text-5xl">
              Rated <em className="italic text-ember">5.0</em> by the people of
              Bhubaneswar.
            </h2>
          </div>

          <div
            data-testid="google-rating-summary"
            className="flex items-center gap-5 rounded-3xl border border-white/10 bg-[#0F192C] p-5 shadow-[0_0_30px_rgba(0,0,0,0.4)] sm:px-7"
          >
            <GoogleMark />
            <div className="border-l border-white/10 pl-5">
              <div className="flex items-center gap-2.5">
                <span className="font-serif text-3xl font-semibold text-white">
                  5.0
                </span>
                <Stars />
              </div>
              <div className="mt-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-mist">
                <BadgeCheck className="h-3.5 w-3.5 text-toothcyan" />
                115+ verified 5.0-star Google reviews
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: EASE }}
        data-testid="reviews-strip"
        className="relative"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-[#070B12] to-transparent"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-[#070B12] to-transparent"
        />
        <div className="overflow-hidden">
          <div
            className="marquee-track flex w-max animate-marquee gap-5 pr-5"
            style={{ animationDuration: "68s" }}
          >
            {doubled.map((r, i) => (
              <figure
                key={i}
                data-testid={i < REVIEWS.length ? `review-card-${i + 1}` : undefined}
                className="w-[300px] shrink-0 rounded-3xl border border-white/10 bg-[#0F192C] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-ember/40 hover:shadow-[0_0_30px_rgba(232,178,92,0.1)] sm:w-[340px]"
              >
                <Stars size="h-3.5 w-3.5" />
                <blockquote className="mt-4 text-sm leading-relaxed text-slate-300">
                  “{r.text}”
                </blockquote>
                <figcaption className="mt-5 flex items-center justify-between gap-3 border-t border-white/10 pt-4">
                  <span className="flex items-center gap-1.5 text-sm font-bold text-ivory">
                    {r.name}
                    <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-toothcyan" />
                  </span>
                  <span className="text-right font-mono text-[9px] uppercase tracking-[0.14em] text-ember">
                    {r.service}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </motion.div>

      <div className="relative mx-auto mt-10 flex max-w-7xl justify-center px-5 sm:px-8">
        <a
          data-testid="reviews-google-link"
          href={CLINIC.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 text-sm font-semibold text-white backdrop-blur transition-all duration-200 hover:border-ember/60 hover:shadow-[0_0_24px_rgba(232,178,92,0.15)] active:scale-[0.97]"
        >
          Read all 115 reviews on Google
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
      <p className="relative mt-4 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-mist/70">
        Quotes sourced from the clinic's public Google listing
      </p>
    </section>
  );
}
